import {
  ACTION_DATA_SOURCES_UNSTRUCTURED_SUMMARY,
  DataSourceSummaryWidgetParams,
  fetchDataSourcesUnstructuredSummary as fetchWidget,
  resetUnstructuredSummaryWidget as resetWidget,
  UnstructuredSummaryWidget
} from './dataSourcesSlice'
import NoSensitiveDataSvg from '../../assets/inlineSvg/noData.svg'
import FolderIcon from '../../assets/inlineSvg/folderIcon.svg'
import MessageIcon from '../../assets/inlineSvg/messageIcon.svg'
import BlobIcon from '../../assets/inlineSvg/blobIcon.svg'
import LoaderView from '../../components/Loader/loader'
import {
  DATA_SOURCE_ID,
  DATA_SOURCE_TYPE,
  DATA_SOURCE_TYPES,
  FILTER_DANGER_KEY,
  FILTER_DATASOURCE_CATEGORY,
  FILTER_RISK_KEY,
  FILTER_UNSTRUCTURED_KEY,
  MIXED_DATASOURCES,
  URL_BLOB,
  URL_DATA_SOURCES,
  URL_DOCUMENTS_ALL,
  URL_INSIGHTS
} from '../../constants'
import useUrlContext from '../../hooks/useUrlContext'
import { getShowLoader } from '../../reducers/requestReducer'
import { RootState } from '../../rootReducer'
import { getUpdatedUrl } from '../../utils/urlUtil'
import { mockUnstructuredSummaryWidgetData } from '../dashboards/renderer/utils/widgetMockdata'
import { getUniqueWidgetIndex, UNIQUE_WIDGET_INDEX } from '../dashboards/mainDashboardv2/constants'
import React, { useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import { Icon, Typography } from '@lightbeamai/design-system'
import './dataSources.less'
import './unstructuredSummaryWidget.less'

interface OwnProps {
  useMockData?: boolean
  isMixedDs?: boolean
  parentUrl?: string
  widgetDatasourceId?: string
  widgetDatasourceType?: DATA_SOURCE_TYPES
  widgetDashboardId?: string
  disableLinks?: boolean
}

interface StateProps {
  widget?: UnstructuredSummaryWidget
  showLoader: boolean
}

interface DispatchProps {
  fetchWidget: (params: DataSourceSummaryWidgetParams & { [UNIQUE_WIDGET_INDEX]: string }) => void
  resetWidget: () => void
}

type UnstructuredSummaryComponentProps = StateProps & DispatchProps & OwnProps

const useOnClickUrls = (
  isMixedDs: boolean,
  parentUrl: string,
  datasourceType: DATA_SOURCE_TYPES,
  datasourceId?: string
) => {
  const intl = useIntl()
  const needsBlobsUrl = [DATA_SOURCE_TYPES.salesForceMarketingCloud].includes(datasourceType)

  const urlRisky = useMemo(() => {
    if (needsBlobsUrl) {
      return getUpdatedUrl({
        pageName: URL_BLOB,
        pathName: parentUrl + URL_BLOB,
        pageQueryParams: {
          [FILTER_DANGER_KEY]: intl.formatMessage({ id: 'filters.risk' })
        },
        queryParams: {
          ...(datasourceType && { [DATA_SOURCE_TYPE]: datasourceType }),
          ...(datasourceId && { [DATA_SOURCE_ID]: datasourceId })
        }
      })
    }

    return getUpdatedUrl({
      pageName: URL_DOCUMENTS_ALL,
      pathName: parentUrl + (isMixedDs ? URL_DOCUMENTS_ALL : URL_INSIGHTS + URL_DOCUMENTS_ALL),
      pageQueryParams: {
        [FILTER_DATASOURCE_CATEGORY]: FILTER_UNSTRUCTURED_KEY,
        [FILTER_RISK_KEY]: 'Risky'
      },
      queryParams: {
        ...(datasourceType && { [DATA_SOURCE_TYPE]: datasourceType }),
        ...(datasourceId && { [DATA_SOURCE_ID]: datasourceId })
      }
    })
  }, [needsBlobsUrl, parentUrl, isMixedDs, intl, datasourceType, datasourceId])

  const urlTotal = useMemo(() => {
    if (needsBlobsUrl) {
      return getUpdatedUrl({
        pageName: URL_BLOB,
        pathName: parentUrl + URL_BLOB,
        pageQueryParams: {},
        queryParams: {
          ...(datasourceType && { [DATA_SOURCE_TYPE]: datasourceType }),
          ...(datasourceId && { [DATA_SOURCE_ID]: datasourceId })
        }
      })
    }

    return getUpdatedUrl({
      pageName: URL_DOCUMENTS_ALL,
      pathName: parentUrl + (isMixedDs ? URL_DOCUMENTS_ALL : URL_INSIGHTS + URL_DOCUMENTS_ALL),
      pageQueryParams: { [FILTER_DATASOURCE_CATEGORY]: FILTER_UNSTRUCTURED_KEY },
      queryParams: {
        ...(datasourceType && { [DATA_SOURCE_TYPE]: datasourceType }),
        ...(datasourceId && { [DATA_SOURCE_ID]: datasourceId })
      }
    })
  }, [needsBlobsUrl, parentUrl, isMixedDs, datasourceType, datasourceId])

  return {
    urlRisky,
    urlTotal
  }
}

const UnstructuredSummaryWidget = (
  props: UnstructuredSummaryComponentProps
): React.ReactElement => {
  const {
    widget,
    showLoader,
    parentUrl = '',
    useMockData = false,
    disableLinks = false,
    fetchWidget,
    resetWidget,
    isMixedDs = false,
    widgetDatasourceId,
    widgetDatasourceType,
    widgetDashboardId
  } = props
  const {
    objectsCount = 0,
    objectsAtRiskCount = 0,
    filesCount = 0,
    messagesCount = 0,
    blobsCount = 0,
    rowsCount = 0
  } = widget || {}

  const intl = useIntl()
  const context = useUrlContext()
  const datasourceId = widgetDatasourceId
    ? widgetDatasourceId
    : context?.contextFilters[DATA_SOURCE_ID]
  const datasourceType = widgetDatasourceType
    ? widgetDatasourceType
    : (context?.queryParams?.datasourceType as DATA_SOURCE_TYPES)
  const isWidgetData = widget && Object.values(widget).some((val) => val > 0)

  // ! This is a workaround for Twilio, I'm not sure why `isMixedDs` hardcoded
  // ! in src/features/dashboards/renderer/utils/widgetMap.tsx
  // ! and `parentUrl` is hardcoded to `${URL_DATA_SOURCES_STRUCTURED}/`
  // ! the URL for Twilio should be URL_DATA_SOURCES
  const formattedParentUrl =
    datasourceType === DATA_SOURCE_TYPES.twilio
      ? `${URL_DATA_SOURCES}/${datasourceId}`
      : isMixedDs
      ? `${parentUrl}${datasourceId} `
      : parentUrl

  const { urlRisky, urlTotal } = useOnClickUrls(
    isMixedDs,
    formattedParentUrl,
    datasourceType,
    datasourceId
  )

  useEffect(() => {
    if (useMockData) return
    fetchWidget({
      datasourceId,
      [UNIQUE_WIDGET_INDEX]: getUniqueWidgetIndex(widgetDashboardId, widgetDatasourceId)
    })
    return () => resetWidget()
  }, [datasourceId, fetchWidget, resetWidget, useMockData])

  const isAirByte = MIXED_DATASOURCES.includes(datasourceType)

  const Comp = disableLinks || datasourceType === DATA_SOURCE_TYPES.salesForce ? 'div' : Link

  return (
    <div className="lb-widget-card unstructured-summary xs-p-16">
      <div className="unstructured-summary-widget-container">
        <div className="xs-mb-16">
          <Typography color="primary" variant="h6">
            {intl.formatMessage({ id: 'dataSources.widget.unstructured.title' })}
          </Typography>
        </div>
        <div
          className="data-sources-summary"
          data-test-id="datasources-unstructured-summary-widget"
        >
          {isWidgetData ? (
            <>
              <div className="main-totals">
                <Comp
                  to={
                    disableLinks || datasourceType === DATA_SOURCE_TYPES.salesForce
                      ? undefined
                      : urlTotal
                  }
                >
                  <div className="summary-item">
                    <Typography
                      className="xs-mb-0"
                      color="primary"
                      type="strong"
                      variant="basePlus6"
                    >
                      {intl.formatNumber(isAirByte ? blobsCount + filesCount : objectsCount, {
                        notation: 'compact'
                      })}
                    </Typography>
                    <Typography color="primary" type="medium" variant="baseMinus2">
                      {intl.formatMessage({ id: 'dataSources.widget.unstructured.objectsTotal' })}
                    </Typography>
                  </div>
                </Comp>
                <Link to={disableLinks ? undefined : urlRisky}>
                  <div className="summary-item">
                    <Typography
                      className="xs-mb-0"
                      color="primary"
                      type="strong"
                      variant="basePlus6"
                    >
                      {intl.formatNumber(objectsAtRiskCount, { notation: 'compact' })}
                    </Typography>
                    <Typography
                      className="at-risk"
                      color="primary"
                      type="strong"
                      variant="baseMinus2"
                    >
                      {intl.formatMessage({ id: 'dataSources.widget.unstructured.objectsAtRisk' })}
                    </Typography>
                  </div>
                </Link>
              </div>
              <div className="totals">
                <div className="item">
                  <div className="item-text-wrapper">
                    <div className="text-icon">
                      <FolderIcon />
                    </div>
                    <Typography color="primary" type="medium" variant="baseMinus2">
                      {intl.formatMessage({ id: 'dataSources.widget.unstructured.documents' })}
                    </Typography>
                  </div>
                  <Typography
                    className="xs-mb-0"
                    color="primary"
                    type="strong"
                    variant="baseMinus2"
                  >
                    {intl.formatNumber(filesCount, { notation: 'compact' })}
                  </Typography>
                </div>

                {!isMixedDs && (
                  <div className="item">
                    <div className="item-text-wrapper">
                      <div className="text-icon">
                        <MessageIcon />
                      </div>
                      <Typography color="primary" type="medium" variant="baseMinus2">
                        {intl.formatMessage({ id: 'dataSources.widget.unstructured.messages' })}
                      </Typography>
                    </div>
                    <Typography
                      className="xs-mb-0"
                      color="primary"
                      type="strong"
                      variant="baseMinus2"
                    >
                      {intl.formatNumber(messagesCount, { notation: 'compact' })}
                    </Typography>
                  </div>
                )}

                <div className="item">
                  <div className="item-text-wrapper">
                    <div className="text-icon">
                      <BlobIcon />
                    </div>
                    <Typography color="primary" type="medium" variant="baseMinus2">
                      {intl.formatMessage({ id: 'dataSources.widget.unstructured.blobs' })}
                    </Typography>
                  </div>
                  <Typography
                    className="xs-mb-0"
                    color="primary"
                    type="strong"
                    variant="baseMinus2"
                  >
                    {intl.formatNumber(blobsCount, { notation: 'compact' })}
                  </Typography>
                </div>
                {datasourceType === DATA_SOURCE_TYPES.twilio && (
                  <div className="item">
                    <div className="item-text-wrapper">
                      <div className="text-icon">
                        <Icon name="Rows01" size={16} />
                      </div>
                      <Typography color="primary" type="medium" variant="baseMinus2">
                        {intl.formatMessage({ id: 'dataSources.widget.unstructured.rows' })}
                      </Typography>
                    </div>
                    <Typography
                      className="xs-mb-0"
                      color="primary"
                      type="strong"
                      variant="baseMinus2"
                    >
                      {intl.formatNumber(rowsCount, { notation: 'compact' })}
                    </Typography>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="entity-empty-state">
              <div className="icon-box xs-mb-12">
                <NoSensitiveDataSvg />
              </div>
              <Typography
                color="placeholder"
                type="medium"
                variant="baseMinus2"
                className="xs-mt-16"
              >
                {intl.formatMessage({ id: 'dashboard.widget.noSensitiveData' })}
              </Typography>
            </div>
          )}
        </div>
        <LoaderView showLoader={showLoader} size="small" />
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => ({
  widget: ownProps?.useMockData
    ? mockUnstructuredSummaryWidgetData
    : state.dataSources.widgetDataUnstructuredSummaryWidget?.[
        getUniqueWidgetIndex(ownProps.widgetDashboardId, ownProps.widgetDatasourceId)
      ],
  showLoader: getShowLoader(ACTION_DATA_SOURCES_UNSTRUCTURED_SUMMARY)
})

const mapDispatchToProps: DispatchProps = { fetchWidget, resetWidget }

export default connect(mapStateToProps, mapDispatchToProps)(UnstructuredSummaryWidget)
